import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, Save, X, AlertCircle, CheckCircle, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiService } from '../services/api';

function SensorManagerModal({ device, sensors, onClose, onSave }) {
    const { t } = useTranslation();
    const [localSensors, setLocalSensors] = useState([...sensors]);
    const [isLoading, setIsLoading] = useState(false);
    const [sensorTypes, setSensorTypes] = useState([]);
    const [showAddSensorPanel, setShowAddSensorPanel] = useState(false);
    const [recommendations, setRecommendations] = useState({});

    // Available pins based on device type
    const availablePins = device?.device_type === 'esp32'
        ? ['A0', 'D0', 'D1', 'D2', 'D4', 'D5', 'D12', 'D13', 'D14', 'D15', 'D16', 'D17', 'D18', 'D19', 'D21', 'D22', 'D23', 'D25', 'D26', 'D27', 'D32', 'D33']
        : ['A0', 'D0', 'D1', 'D2', 'D3', 'D4', 'D5', 'D6', 'D7', 'D8'];

    // Fetch available sensor types and recommendations
    useEffect(() => {
        const fetchSensorTypes = async () => {
            try {
                console.log('Fetching sensor types...');
                const response = await apiService.getSensorTypes();
                console.log('Sensor types response:', response);
                const types = Array.isArray(response) ? response : (response?.sensor_types || []);
                console.log('Parsed sensor types:', types);

                if (!types || types.length === 0) {
                    console.warn('No sensor types found!');
                    toast.error(t('deviceDetail.sensorManager.noSensorTypes', 'No sensor types available. Please contact administrator.'));
                } else {
                    setSensorTypes(types);
                    console.log(`Loaded ${types.length} sensor types`);
                }
            } catch (error) {
                console.error('Failed to fetch sensor types:', error);
                console.error('Error details:', error.response?.data || error.message);
                toast.error(t('deviceDetail.sensorManager.fetchTypesError', `Failed to load sensor types: ${error.response?.data?.error || error.message}`));
            }
        };

        const fetchRecommendations = async () => {
            try {
                // Fetch recommended thresholds for each sensor based on historical data
                const recs = {};
                for (const sensor of sensors) {
                    try {
                        const response = await apiService.getSensorRecommendations(device.id, sensor.pin, '30d');
                        if (response && response.recommendations && response.recommendations.hasEnoughData) {
                            recs[sensor.pin] = response.recommendations.recommendations;
                        }
                    } catch (error) {
                        console.log(`No recommendations available for sensor ${sensor.pin}`);
                    }
                }
                setRecommendations(recs);
            } catch (error) {
                console.error('Failed to fetch recommendations:', error);
            }
        };

        fetchSensorTypes();
        if (device && sensors.length > 0) {
            fetchRecommendations();
        }
    }, [t, device, sensors]);

    // Get used pins
    const usedPins = useMemo(() => {
        return localSensors.reduce((acc, sensor) => {
            if (!acc[sensor.pin]) acc[sensor.pin] = [];
            acc[sensor.pin].push(sensor);
            return acc;
        }, {});
    }, [localSensors]);

    // Check for pin conflicts
    const pinConflicts = useMemo(() => {
        return Object.entries(usedPins)
            .filter(([_, sensors]) => sensors.length > 1)
            .map(([pin]) => pin);
    }, [usedPins]);

    // Get sensor type details
    const getSensorTypeDetails = (typeId) => {
        return sensorTypes.find(t => t.id === typeId);
    };

    const handleAddSensorType = (sensorType) => {
        // Find first available pin
        const usedPinsList = localSensors.map(s => s.pin);
        const nextAvailablePin = availablePins.find(pin => !usedPinsList.includes(pin));

        if (!nextAvailablePin) {
            toast.error(t('deviceDetail.sensorManager.noPinsAvailable', 'No available pins. Please remove a sensor first.'));
            return;
        }

        const newSensor = {
            id: `new_${Date.now()}`,
            pin: nextAvailablePin,
            sensor_type_id: sensorType.id,
            sensor_type: sensorType.name,
            name: `${sensorType.name} on ${nextAvailablePin}`,
            enabled: true,
            calibration_offset: 0,
            sensitivity: 50, // Default sensitivity (0-100 scale, 50 = medium)
            threshold_min: sensorType.default_min || 0,
            threshold_max: sensorType.default_max || 1000,
            isNew: true
        };

        setLocalSensors([...localSensors, newSensor]);
        setShowAddSensorPanel(false);
        toast.success(t('deviceDetail.sensorManager.sensorAdded', 'Sensor added'));
    };

    const handleRemoveSensor = (sensorId) => {
        setLocalSensors(localSensors.filter(s => s.id !== sensorId));
    };

    const handleSensorChange = (sensorId, field, value) => {
        setLocalSensors(localSensors.map(s =>
            s.id === sensorId ? { ...s, [field]: value } : s
        ));
    };

    const handlePinChange = (sensorId, newPin) => {
        // Check if pin is already used
        const isUsed = localSensors.some(s => s.id !== sensorId && s.pin === newPin);
        if (isUsed) {
            toast.error(t('deviceDetail.sensorManager.pinInUse', 'Pin {{pin}} is already in use', { pin: newPin }));
            return;
        }
        handleSensorChange(sensorId, 'pin', newPin);
    };

    const handleSave = async () => {
        // Validate no pin conflicts
        if (pinConflicts.length > 0) {
            toast.error(t('deviceDetail.sensorManager.pinConflictsError', 'Please resolve pin conflicts before saving'));
            return;
        }

        setIsLoading(true);
        try {
            // Delete removed sensors
            const removedSensors = sensors.filter(s => !localSensors.find(ls => ls.id === s.id));
            for (const sensor of removedSensors) {
                if (!sensor.id.toString().startsWith('new_')) {
                    try {
                        await apiService.deleteSensor(device.id, sensor.id);
                        console.log(`Deleted sensor ${sensor.id}`);
                    } catch (error) {
                        console.error(`Failed to delete sensor ${sensor.id}:`, error);
                        throw error;
                    }
                }
            }

            // Create or update sensors
            for (const sensor of localSensors) {
                let sensorId = sensor.id;

                if (sensor.isNew) {
                    // Create new sensor
                    try {
                        // Ensure numeric values are properly converted or set to defaults
                        const createData = {
                            pin: parseInt(sensor.pin),
                            sensor_type_id: parseInt(sensor.sensor_type_id),
                            name: sensor.name,
                            enabled: sensor.enabled,
                            calibration_offset: sensor.calibration_offset != null && sensor.calibration_offset !== ''
                                ? parseFloat(sensor.calibration_offset)
                                : 0,
                            sensitivity: sensor.sensitivity != null && sensor.sensitivity !== ''
                                ? parseInt(sensor.sensitivity)
                                : 50,
                            threshold_min: sensor.threshold_min != null && sensor.threshold_min !== ''
                                ? parseFloat(sensor.threshold_min)
                                : null,
                            threshold_max: sensor.threshold_max != null && sensor.threshold_max !== ''
                                ? parseFloat(sensor.threshold_max)
                                : null
                        };

                        const response = await apiService.createSensor(device.id, createData);
                        sensorId = response.sensor?.id || response.data?.sensor?.id || response.id;
                        console.log(`Created sensor ${sensor.name} with ID ${sensorId}`);
                    } catch (error) {
                        console.error(`Failed to create sensor ${sensor.name}:`, error);
                        toast.error(t('deviceDetail.sensorManager.createError', `Failed to create sensor ${sensor.name}: ${error.response?.data?.error || error.message}`));
                        throw error;
                    }
                } else {
                    // Update existing sensor
                    try {
                        // Ensure numeric values are properly converted or set to null
                        const updateData = {
                            name: sensor.name,
                            enabled: sensor.enabled,
                            calibration_offset: sensor.calibration_offset != null && sensor.calibration_offset !== ''
                                ? parseFloat(sensor.calibration_offset)
                                : 0,
                            sensitivity: sensor.sensitivity != null && sensor.sensitivity !== ''
                                ? parseInt(sensor.sensitivity)
                                : 50,
                            threshold_min: sensor.threshold_min != null && sensor.threshold_min !== ''
                                ? parseFloat(sensor.threshold_min)
                                : null,
                            threshold_max: sensor.threshold_max != null && sensor.threshold_max !== ''
                                ? parseFloat(sensor.threshold_max)
                                : null,
                            trigger_ota: false
                        };

                        await apiService.updateSensor(device.id, sensor.id, updateData);
                        console.log(`Updated sensor ${sensor.id}`);
                    } catch (error) {
                        console.error(`Failed to update sensor ${sensor.id}:`, error);
                        toast.error(t('deviceDetail.sensorManager.updateError', `Failed to update sensor ${sensor.name}: ${error.response?.data?.error || error.message}`));
                        throw error;
                    }
                }
            }

            toast.success(t('deviceDetail.sensorManager.saveSuccess', 'Sensor configuration saved! Device will update on next heartbeat.'));
            onSave();
        } catch (error) {
            console.error('Failed to save sensors:', error);
            const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
            toast.error(t('deviceDetail.sensorManager.saveError', `Failed to save sensor configuration: ${errorMessage}`));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={onClose}
        >
            <div
                className="w-full max-w-6xl max-h-[90vh] overflow-hidden rounded-xl bg-white shadow-2xl flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="border-b border-gray-200 bg-gradient-to-r from-indigo-600 to-indigo-700 p-6 text-white">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-2xl font-bold">
                                {t('deviceDetail.sensorManager.title', 'Manage Sensors')}
                            </h3>
                            <p className="mt-1 text-sm text-indigo-100">
                                {t('deviceDetail.sensorManager.subtitle', 'Configure sensors for {{device}}', { device: device?.name || device?.id })}
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="rounded-full p-2 text-white/80 hover:bg-white/20 hover:text-white"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {/* Pin Conflicts Warning */}
                    {pinConflicts.length > 0 && (
                        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4">
                            <div className="flex items-start gap-3">
                                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                                <div>
                                    <h4 className="font-semibold text-red-900">
                                        {t('deviceDetail.sensorManager.pinConflicts', 'Pin Conflicts Detected')}
                                    </h4>
                                    <p className="text-sm text-red-700 mt-1">
                                        {t('deviceDetail.sensorManager.pinConflictsDesc', 'The following pins are used by multiple sensors: {{pins}}', {
                                            pins: pinConflicts.join(', ')
                                        })}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Add Sensor Section */}
                    {!showAddSensorPanel && (
                        <button
                            onClick={() => setShowAddSensorPanel(true)}
                            className="mb-6 w-full inline-flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-indigo-300 bg-indigo-50 px-6 py-4 text-sm font-medium text-indigo-700 hover:border-indigo-400 hover:bg-indigo-100 transition-colors"
                        >
                            <Plus className="h-5 w-5" />
                            {t('deviceDetail.sensorManager.addNewSensor', 'Add New Sensor')}
                        </button>
                    )}

                    {/* Sensor Type Picker */}
                    {showAddSensorPanel && (
                        <div className="mb-6 rounded-xl border-2 border-indigo-200 bg-indigo-50 p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h4 className="text-lg font-semibold text-gray-900">
                                    {t('deviceDetail.sensorManager.selectSensorType', 'Select Sensor Type')}
                                </h4>
                                <button
                                    onClick={() => setShowAddSensorPanel(false)}
                                    className="text-gray-500 hover:text-gray-700"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>
                            {sensorTypes.length === 0 ? (
                                <div className="text-center py-8">
                                    <AlertCircle className="h-12 w-12 mx-auto mb-3 text-amber-500" />
                                    <p className="text-sm text-gray-600 mb-2">
                                        {t('deviceDetail.sensorManager.noSensorTypesAvailable', 'No sensor types available')}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                        {t('deviceDetail.sensorManager.consoleHint', 'Check browser console for errors or contact the administrator')}
                                    </p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {sensorTypes.map((sensorType) => {
                                        const count = localSensors.filter(s => s.sensor_type_id === sensorType.id).length;
                                        return (
                                            <div
                                                key={sensorType.id}
                                                className="border border-gray-200 rounded-lg p-4 bg-white hover:shadow-md hover:border-indigo-300 transition-all cursor-pointer"
                                                onClick={() => handleAddSensorType(sensorType)}
                                            >
                                                <div className="flex items-center justify-between mb-2">
                                                    <h5 className="font-semibold text-gray-900">{sensorType.name}</h5>
                                                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${sensorType.pin_type === 'analog'
                                                        ? 'bg-green-100 text-green-800'
                                                        : 'bg-blue-100 text-blue-800'
                                                        }`}>
                                                        {sensorType.pin_type
                                                            ? t(`deviceDetail.sensorManager.pinType.${sensorType.pin_type}`, sensorType.pin_type)
                                                            : t('deviceDetail.sensorManager.pinType.digital', 'digital')}
                                                    </span>
                                                </div>
                                                <p className="text-sm text-gray-600 mb-3">
                                                    {sensorType.description || t('deviceDetail.sensorManager.unitLabel', 'Unit: {{unit}}', { unit: sensorType.unit || t('common.notAvailable', 'N/A') })}
                                                </p>
                                                <div className="flex items-center justify-between">
                                                    <span className="text-xs text-gray-500">
                                                        {t('deviceDetail.sensorManager.configuredCount', '{{count}} configured', { count })}
                                                    </span>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleAddSensorType(sensorType);
                                                        }}
                                                        className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-3 py-1 text-xs font-medium text-white hover:bg-indigo-700"
                                                    >
                                                        <Plus className="h-3 w-3" />
                                                        {t('common.add', 'Add')}
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Configured Sensors */}
                    <div className="space-y-4">
                        {localSensors.length === 0 ? (
                            <div className="text-center py-12 text-gray-500">
                                <Info className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                                <p>{t('deviceDetail.sensorManager.noSensors', 'No sensors configured yet. Click "Add New Sensor" to get started.')}</p>
                            </div>
                        ) : (
                            localSensors.map((sensor) => {
                                const sensorTypeDetails = getSensorTypeDetails(sensor.sensor_type_id);
                                const hasPinConflict = pinConflicts.includes(sensor.pin);

                                return (
                                    <div
                                        key={sensor.id}
                                        className={`rounded-xl border-2 ${hasPinConflict
                                            ? 'border-red-300 bg-red-50'
                                            : 'border-gray-200 bg-white'
                                            } shadow-sm hover:shadow-md transition-shadow`}
                                    >
                                        {/* Sensor Header */}
                                        <div className="p-5 bg-gradient-to-r from-gray-50 to-gray-100">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center font-bold text-white ${hasPinConflict ? 'bg-red-500' : 'bg-indigo-600'
                                                        }`}>
                                                        {sensor.pin}
                                                    </div>
                                                    <div>
                                                        <h4 className="font-semibold text-gray-900">{sensor.name}</h4>
                                                        <p className="text-sm text-gray-600">
                                                            {sensorTypeDetails?.name || sensor.sensor_type} • {sensorTypeDetails?.unit || ''}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <label className="flex items-center gap-2 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={sensor.enabled}
                                                            onChange={(e) => handleSensorChange(sensor.id, 'enabled', e.target.checked)}
                                                            className="h-5 w-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                                        />
                                                        <span className="text-sm font-medium text-gray-700">
                                                            {sensor.enabled ? (
                                                                <span className="flex items-center gap-1 text-green-600">
                                                                    <CheckCircle className="h-4 w-4" />
                                                                    {t('common.enabled', 'Enabled')}
                                                                </span>
                                                            ) : (
                                                                <span className="text-gray-500">{t('common.disabled', 'Disabled')}</span>
                                                            )}
                                                        </span>
                                                    </label>
                                                    <button
                                                        onClick={() => handleRemoveSensor(sensor.id)}
                                                        className="rounded-full p-2 text-red-600 hover:bg-red-100 transition-colors"
                                                        title={t('deviceDetail.sensorManager.remove', 'Remove Sensor')}
                                                    >
                                                        <Trash2 className="h-5 w-5" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Sensor Configuration */}
                                        <div className="p-5 space-y-4">
                                            {/* Basic Settings */}
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        {t('deviceDetail.sensorManager.pin', 'Pin')} {hasPinConflict && <span className="text-red-600">*</span>}
                                                    </label>
                                                    <select
                                                        value={sensor.pin}
                                                        onChange={(e) => handlePinChange(sensor.id, e.target.value)}
                                                        className={`w-full rounded-lg border ${hasPinConflict ? 'border-red-300' : 'border-gray-300'
                                                            } px-4 py-2 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200`}
                                                    >
                                                        {availablePins.map(pin => (
                                                            <option key={pin} value={pin}>
                                                                {pin}
                                                                {usedPins[pin]?.length > 0 && pin !== sensor.pin && (
                                                                    <> {t('deviceDetail.sensorManager.pinInUse', '(in use)')}</>
                                                                )}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                                <div className="md:col-span-2">
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        {t('deviceDetail.sensorManager.name', 'Sensor Name')}
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={sensor.name}
                                                        onChange={(e) => handleSensorChange(sensor.id, 'name', e.target.value)}
                                                        className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                                                        placeholder={t('deviceDetail.sensorManager.namePlaceholder', 'e.g., Living Room Temperature')}
                                                    />
                                                </div>
                                            </div>

                                            {/* Sensitivity & Calibration */}
                                            <div className="rounded-lg bg-blue-50 border border-blue-200 p-4">
                                                <h5 className="text-sm font-semibold text-blue-900 mb-3 flex items-center gap-2">
                                                    <Info className="h-4 w-4" />
                                                    {t('deviceDetail.sensorManager.sensitivityCalibration', 'Sensitivity & Calibration')}
                                                </h5>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            {t('deviceDetail.sensorManager.sensitivity', 'Sensitivity')} ({sensor.sensitivity || 50}/100)
                                                        </label>
                                                        <input
                                                            type="range"
                                                            min="0"
                                                            max="100"
                                                            value={sensor.sensitivity || 50}
                                                            onChange={(e) => handleSensorChange(sensor.id, 'sensitivity', parseInt(e.target.value))}
                                                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                                                        />
                                                        <div className="flex justify-between text-xs text-gray-600 mt-1">
                                                            <span>{t('deviceDetail.sensorManager.low', 'Low')}</span>
                                                            <span>{t('deviceDetail.sensorManager.medium', 'Medium')}</span>
                                                            <span>{t('deviceDetail.sensorManager.high', 'High')}</span>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            {t('deviceDetail.sensorManager.offset', 'Calibration Offset')}
                                                        </label>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            value={sensor.calibration_offset || 0}
                                                            onChange={(e) => handleSensorChange(sensor.id, 'calibration_offset', parseFloat(e.target.value) || 0)}
                                                            className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                                                        />
                                                        <p className="text-xs text-gray-600 mt-1">
                                                            {t('deviceDetail.sensorManager.offsetHint', 'Add/subtract from raw value')}
                                                        </p>
                                                    </div>
                                                </div>
                                                <p className="text-xs text-gray-600 mt-2">
                                                    {t('deviceDetail.sensorManager.calibrationFormula', 'Final value = raw + offset')}
                                                </p>
                                            </div>

                                            {/* Thresholds */}
                                            <div className="rounded-lg bg-amber-50 border border-amber-200 p-4">
                                                <div className="flex items-center justify-between mb-3">
                                                    <h5 className="text-sm font-semibold text-amber-900">
                                                        {t('deviceDetail.sensorManager.thresholds', 'Thresholds (for device-side alerts)')}
                                                    </h5>
                                                    {recommendations[sensor.pin] && (
                                                        <button
                                                            onClick={() => {
                                                                const rec = recommendations[sensor.pin];
                                                                handleSensorChange(sensor.id, 'threshold_min', rec.optimal?.min || rec.warning?.min || 0);
                                                                handleSensorChange(sensor.id, 'threshold_max', rec.optimal?.max || rec.warning?.max || 1000);
                                                                toast.success(t('deviceDetail.sensorManager.recommendationsApplied', 'Recommended thresholds applied'));
                                                            }}
                                                            className="text-xs bg-green-600 text-white px-3 py-1 rounded-md hover:bg-green-700 transition-colors"
                                                        >
                                                            {t('deviceDetail.sensorManager.useRecommended', 'Use Recommended')}
                                                        </button>
                                                    )}
                                                </div>
                                                {recommendations[sensor.pin] && (
                                                    <div className="mb-3 text-xs text-green-700 bg-green-50 border border-green-200 rounded p-2">
                                                        <strong>{t('deviceDetail.sensorManager.recommended', 'Recommended')}:</strong>{' '}
                                                        Min: {recommendations[sensor.pin].optimal?.min?.toFixed(2) || recommendations[sensor.pin].warning?.min?.toFixed(2)},{' '}
                                                        Max: {recommendations[sensor.pin].optimal?.max?.toFixed(2) || recommendations[sensor.pin].warning?.max?.toFixed(2)}
                                                        {' '}(based on 30-day history)
                                                    </div>
                                                )}
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            {t('deviceDetail.sensorManager.minThreshold', 'Minimum')}
                                                        </label>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            value={sensor.threshold_min || 0}
                                                            onChange={(e) => handleSensorChange(sensor.id, 'threshold_min', parseFloat(e.target.value) || 0)}
                                                            className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            {t('deviceDetail.sensorManager.maxThreshold', 'Maximum')}
                                                        </label>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            value={sensor.threshold_max || 1000}
                                                            onChange={(e) => handleSensorChange(sensor.id, 'threshold_max', parseFloat(e.target.value) || 1000)}
                                                            className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 flex justify-between items-center">
                    <div className="text-sm text-gray-600">
                        <p>{t('deviceDetail.sensorManager.configNote', 'Config pushed to device on next heartbeat (~1-5 min)')}</p>
                        {pinConflicts.length > 0 && (
                            <p className="text-red-600 font-medium mt-1">
                                ⚠️ {t('deviceDetail.sensorManager.resolvePinConflicts', 'Resolve pin conflicts before saving')}
                            </p>
                        )}
                    </div>
                    <div className="flex gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-lg border border-gray-300 px-6 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                            disabled={isLoading}
                        >
                            {t('deviceDetail.sensorManager.cancel', 'Cancel')}
                        </button>
                        <button
                            onClick={handleSave}
                            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60 transition-colors shadow-lg shadow-indigo-500/30"
                            disabled={isLoading || pinConflicts.length > 0}
                        >
                            <Save className="h-4 w-4" />
                            {isLoading ? t('deviceDetail.sensorManager.saving', 'Saving...') : t('deviceDetail.sensorManager.save', 'Save & Push Config')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SensorManagerModal;
